import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DWELL_THRESHOLD_SEC, useDwellForLesson, useLessonDwell } from '../../store/lessonDwell';
import { useLessonViewSession } from '../../lib/useLessonViewSession';
import {
  claimBonusLesson,
  claimSkipAhead,
  setLessonCompleted,
  setLessonReflection,
  setLessonReflectionNote,
  setLessonSaved,
  useCurrentStreak,
  useCurrentUserProfile,
  useLessonHelpfulStats,
  useTodaysLesson,
  type LessonRow,
} from '@busy/api';
import { localizeLesson, type LocalizedLesson } from '@busy/core';
import { useToast } from '@busy/ui';
import { useUI } from '../../store/ui';
import { useT } from '../../lib/i18n';
import { richText } from '../../lib/richText';

type Vote = 'yes' | 'no';

// Maps the DB's Title-Case category strings to dictionary keys (cat.*).
// Anything unmapped falls through to the raw category string uppercased.
function categoryKey(category: string): string {
  switch (category.toLowerCase()) {
    case 'retention': return 'cat.retention';
    case 'skill': return 'cat.skill';
    case 'customer service': return 'cat.customerService';
    case 'time management': return 'cat.timeManagement';
    case 'pricing': return 'cat.pricing';
    case 'marketing': return 'cat.marketing';
    default: return '';
  }
}

// Daily lesson card — matches the demo's structure:
//   .lesson-banner — navy gradient header w/ category eyebrow + title + hook, gold-500
//                    underline as a 3px bottom border
//   .lesson-body   — surface-bg body w/ insight / play / why / expect (expandable)
//   .lesson-actions — bg-alt footer with reflect-row + complete button
//
// Reflections persist to user_lesson_state; saved/completed too.
export function DailyLessonCard() {
  const today = useTodaysLesson();
  const toast = useToast();
  const t = useT();
  const language = useUI((s) => s.language);
  // Start collapsed by default so Today opens to a scannable, compact
  // view (lesson title + hook only). The barber taps "Read more" to
  // expand into the full lesson — same one-tap interaction either way,
  // but the screen is calmer on entry.
  const [expanded, setExpanded] = useState(false);
  const [submittingVote, setSubmittingVote] = useState<Vote | null>(null);
  // Dwell timer is global (only one lesson can be timing at once). The store
  // starts/pauses based on expansion state below. Selector returns only this
  // lesson's accumulated seconds when it's the active one — 0 otherwise.
  const todaysLessonId = today.data?.lesson?.id ?? null;
  const { secs: dwellSec, ready: dwellReady } = useDwellForLesson(todaysLessonId ?? '');
  const dwellOpen = useLessonDwell((s) => s.open);
  const dwellClose = useLessonDwell((s) => s.close);
  useEffect(() => {
    if (!todaysLessonId) return;
    if (expanded) {
      dwellOpen(todaysLessonId);
    } else {
      dwellClose(todaysLessonId);
    }
    return () => {
      // Unmount (navigating away) should pause the timer too.
      if (todaysLessonId) dwellClose(todaysLessonId);
    };
  }, [expanded, todaysLessonId, dwellOpen, dwellClose]);

  // Lesson view session — persists each (open, close) cycle to
  // lesson_view_sessions for engagement-depth analysis. Separate from
  // the in-memory dwell timer above: dwell is for the gamification
  // gate ("you read it long enough to mark complete"); the view
  // session log is the analytics record.
  useLessonViewSession(todaysLessonId, expanded);

  if (today.loading) {
    return <div className="h-48 rounded-2xl bg-bg-alt animate-pulse" />;
  }

  if (today.error) {
    return (
      <div className="rounded-xl border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm">
        {t('lesson.errorLoad')} {today.error}
      </div>
    );
  }

  if (!today.data) {
    return (
      <article className="rounded-2xl bg-surface border border-line shadow-sm overflow-hidden">
        <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white p-6 border-b-[3px] border-gold-500">
          <div className="text-[11px] font-extrabold tracking-[0.18em] uppercase text-gold-400 mb-1.5">
            {t('lesson.eyebrow')}
          </div>
          <h2 className="font-display text-3xl leading-tight">{t('lesson.empty.title')}</h2>
        </div>
        <div className="p-6 text-sm text-ink-700">
          {t('lesson.empty.body', {
            file: 'supabase/migrations/0002_seed_lessons.sql',
          })
            .split(/(supabase\/migrations\/0002_seed_lessons\.sql)/)
            .map((chunk, i) =>
              chunk === 'supabase/migrations/0002_seed_lessons.sql' ? (
                <code key={i} className="bg-bg-alt px-1 rounded text-xs">
                  {chunk}
                </code>
              ) : (
                <span key={i}>{chunk}</span>
              ),
            )}
        </div>
      </article>
    );
  }

  // Resolve translations.es (or whatever language is active) once at the top of
  // render — every downstream field on `lesson` is then in the user's language.
  const lesson = localizeLesson(today.data.lesson, language);
  const state = today.data.state;
  const currentVote: Vote | null = (state?.reflection as Vote | null) ?? null;
  const isSaved = state?.saved ?? false;
  const isCompleted = state?.completed ?? false;
  const catKey = categoryKey(lesson.category);
  const catLabel = catKey ? t(catKey).toUpperCase() : lesson.category.toUpperCase();

  // Soft gate on "Mark complete": a 20-second dwell timer that only ticks while
  // the lesson is expanded. Voting is optional (helpful poll is just feedback,
  // not a completion gate). Already-completed lessons stay tappable so users
  // can toggle off.
  const completeReady = isCompleted || dwellReady;
  const completeBlockedReason = expanded
    ? t('lesson.complete.needDwell', { sec: Math.max(0, DWELL_THRESHOLD_SEC - dwellSec) })
    : t('lesson.complete.openFirst');

  async function handleVote(v: Vote) {
    setSubmittingVote(v);
    const { error } = await setLessonReflection(lesson.id, v);
    setSubmittingVote(null);
    if (error) {
      toast(t('lesson.toast.errorVote', { message: error.message }), 'error');
      return;
    }
    toast(t(`lesson.toast.voted.${v}`), 'success');
  }

  async function handleToggleSave() {
    const { error } = await setLessonSaved(lesson.id, !isSaved);
    if (error) toast(t('lesson.toast.errorBookmark', { message: error.message }), 'error');
    else toast(t(isSaved ? 'lesson.toast.removed' : 'lesson.toast.saved'), 'success');
  }

  async function handleToggleComplete() {
    const { error } = await setLessonCompleted(lesson.id, !isCompleted);
    if (error) toast(t('lesson.toast.errorComplete', { message: error.message }), 'error');
    else
      toast(
        t(isCompleted ? 'lesson.toast.markedIncomplete' : 'lesson.toast.markedComplete'),
        'success',
      );
  }

  return (
    <article className="rounded-2xl bg-surface border border-line shadow-sm overflow-hidden">
      {/* Banner — navy gradient with gold underline */}
      <div className="relative bg-gradient-to-br from-navy-900 to-navy-800 text-white p-6 md:p-7 border-b-[3px] border-gold-500">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] font-extrabold tracking-[0.18em] uppercase text-gold-400 inline-flex items-center gap-2">
            {t('lesson.eyebrow')}
            {/* paddingLeft adds matching space to the left to balance the
                trailing letter-spacing (inherited 0.18em tracking from the
                eyebrow div). Without it the visible text reads ~2px left
                of the pill's geometric center. */}
            <span
              className="bg-white/10 text-gold-200 py-0.5 pr-1.5 rounded text-[10px]"
              style={{ paddingLeft: 'calc(0.375rem + 0.18em)' }}
            >
              {catLabel}
            </span>
            {lesson.high_impact && (
              <span
                className="bg-gold-500 text-navy-900 py-0.5 pr-1.5 rounded text-[10px]"
                style={{ paddingLeft: 'calc(0.375rem + 0.18em)' }}
              >
                {t('lesson.highImpact')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {lesson.duration && (
              <span className="text-[11px] font-bold text-white/70">{lesson.duration}</span>
            )}
            <button
              type="button"
              onClick={handleToggleSave}
              aria-label={isSaved ? t('lesson.bookmark.remove') : t('lesson.bookmark.save')}
              className={`w-7 h-7 rounded-full grid place-items-center transition-colors ${
                isSaved
                  ? 'bg-gold-500 text-navy-900'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              <BookmarkIcon className="w-3.5 h-3.5" filled={isSaved} />
            </button>
          </div>
        </div>
        <h2 className="font-display text-3xl md:text-4xl leading-[1.05] mt-3 max-w-[620px]">
          {lesson.title}
        </h2>
        {lesson.hook && (
          <p className="text-white/72 text-[15px] leading-relaxed mt-2.5 max-w-[620px]">
            {richText(lesson.hook)}
          </p>
        )}
      </div>

      {/* Body — expandable lesson content */}
      <div className="p-6 md:p-7">
        {expanded && <ExpandedContent lesson={lesson} />}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-extrabold uppercase tracking-[0.12em] text-navy-900 dark:text-gold-500 inline-flex items-center gap-1 hover:opacity-70"
        >
          {expanded ? t('lesson.showLess') : t('lesson.readMore')}
          <ChevronIcon className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Footer — bg-alt with helpful poll + complete button */}
      <div className="border-t border-line bg-bg-alt px-5 py-4 md:px-7 md:py-5 space-y-3">
        <HelpfulPoll
          lessonId={lesson.id}
          currentVote={currentVote}
          submittingVote={submittingVote}
          onVote={handleVote}
          existingNote={state?.reflection_note ?? null}
        />
        <button
          type="button"
          onClick={handleToggleComplete}
          disabled={!completeReady}
          className={`w-full h-10 rounded-lg font-bold text-sm inline-flex items-center justify-center gap-2 transition-colors ${
            isCompleted
              ? 'bg-green-600 text-white hover:bg-green-700'
              : completeReady
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-navy-900/35 text-white/85 cursor-not-allowed'
          }`}
        >
          <CheckIcon className="w-4 h-4" />
          {isCompleted
            ? t('lesson.completed')
            : completeReady
            ? t('lesson.markComplete')
            : completeBlockedReason}
        </button>

        {/* Engagement bonuses — only after the day's lesson is completed.
            Read another = un-delivered goal-aligned bonus.
            Skip ahead    = pulls tomorrow's lesson; gated by 7+ day streak
                            and a 1/week cooldown server-side. */}
        {isCompleted && <PostCompletionBonuses />}
      </div>
    </article>
  );
}

function PostCompletionBonuses() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const profile = useCurrentUserProfile();
  const streak = useCurrentStreak();
  const [busy, setBusy] = useState<'bonus' | 'skip' | null>(null);

  const lastSkip = profile.data?.last_skip_ahead_at ?? null;
  const skipReady =
    (streak.data?.current_streak ?? 0) >= 7 &&
    (!lastSkip || new Date(lastSkip).getTime() < Date.now() - 7 * 86_400_000);

  // 24h cooldown on the Read-another bonus mirrors the server-side gate
  // in claimBonusLesson — checking client-side just lets us disable the
  // button + show a friendly "back tomorrow" tooltip instead of waiting
  // for the server to reject.
  const lastBonus = profile.data?.last_bonus_lesson_at ?? null;
  const bonusReady =
    !lastBonus || new Date(lastBonus).getTime() < Date.now() - 24 * 3_600_000;
  const bonusReturnsAt = lastBonus
    ? new Date(new Date(lastBonus).getTime() + 24 * 3_600_000)
    : null;

  async function handleBonus() {
    setBusy('bonus');
    const { data, error } = await claimBonusLesson();
    setBusy(null);
    if (error) {
      // Server-enforced cooldown — show the friendly variant.
      if (error.message === 'BONUS_COOLDOWN') {
        toast(t('lesson.bonus.cooldownToast'), 'info');
        return;
      }
      toast(t('lesson.bonus.error', { message: error.message }), 'error');
      return;
    }
    if (!data) {
      toast(t('lesson.bonus.empty'), 'info');
      return;
    }
    toast(t('lesson.bonus.unlocked', { title: data.title }), 'success');
    navigate(`/lessons?expand=${encodeURIComponent(data.id)}`);
  }

  async function handleSkip() {
    setBusy('skip');
    const { data, error } = await claimSkipAhead();
    setBusy(null);
    if (error) {
      toast(t('lesson.skip.error', { message: error.message }), 'error');
      return;
    }
    if (!data) {
      toast(t('lesson.skip.empty'), 'info');
      return;
    }
    toast(t('lesson.skip.unlocked', { title: data.title }), 'success');
    navigate(`/lessons?expand=${encodeURIComponent(data.id)}`);
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
      <button
        type="button"
        onClick={handleBonus}
        disabled={busy != null || !bonusReady}
        title={
          bonusReady
            ? t('lesson.bonus.tooltip')
            : t('lesson.bonus.cooldownTooltip', {
                hours: bonusReturnsAt
                  ? Math.max(1, Math.ceil((bonusReturnsAt.getTime() - Date.now()) / 3_600_000))
                  : 24,
              })
        }
        className={`h-10 rounded-lg border-2 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 ${
          bonusReady
            ? 'border-gold-500 bg-gold-500/10 text-navy-900 dark:text-gold-300'
            : 'border-line bg-bg-alt text-ink-500 cursor-not-allowed'
        }`}
      >
        <SparkIcon className={`w-4 h-4 ${bonusReady ? 'text-gold-500' : 'text-ink-500'}`} />
        {busy === 'bonus'
          ? t('lesson.bonus.loading')
          : !bonusReady
          ? t('lesson.bonus.cooldownLabel')
          : t('lesson.bonus.cta')}
      </button>
      <button
        type="button"
        onClick={handleSkip}
        disabled={!skipReady || busy != null}
        title={skipReady ? t('lesson.skip.tooltip') : t('lesson.skip.locked')}
        className={`h-10 rounded-lg border-2 font-bold text-sm inline-flex items-center justify-center gap-2 ${
          skipReady
            ? 'border-navy-900 dark:border-gold-500 bg-navy-900/5 dark:bg-gold-500/10 text-navy-900 dark:text-gold-300 disabled:opacity-50'
            : 'border-line bg-bg-alt text-ink-500 cursor-not-allowed'
        }`}
      >
        <ForwardIcon className="w-4 h-4" />
        {busy === 'skip'
          ? t('lesson.skip.loading')
          : skipReady
          ? t('lesson.skip.cta')
          : t('lesson.skip.gateLabel')}
      </button>
    </div>
  );
}

function SparkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l1.5 6.5L20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5z" />
    </svg>
  );
}

function ForwardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="13 19 22 12 13 5 13 19" />
      <polygon points="2 19 11 12 2 5 2 19" />
    </svg>
  );
}

function ExpandedContent({ lesson }: { lesson: LocalizedLesson<LessonRow> }) {
  const plays = (lesson.plays ?? []) as Array<{ t: string; d: string }>;
  const t = useT();
  return (
    <div className="space-y-5 mb-5 text-[15px] leading-relaxed text-ink-700">
      {lesson.insight && <Section label={t('lesson.section.insight')}>{richText(lesson.insight)}</Section>}
      {plays.length > 0 && (
        <div>
          <SectionLabel>{t('lesson.section.play')}</SectionLabel>
          <ol className="space-y-2 list-decimal pl-4 text-ink-700">
            {plays.map((p, i) => (
              <li key={i} className="leading-relaxed">
                <strong className="text-ink-900">{richText(p.t)}</strong>{' '}
                <span>{richText(p.d)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {lesson.why && <Section label={t('lesson.section.why')}>{richText(lesson.why)}</Section>}
      {lesson.expect && <Section label={t('lesson.section.expect')}>{richText(lesson.expect)}</Section>}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[12px] font-extrabold uppercase tracking-[0.14em] text-ink-500 mb-2">
      {children}
    </h3>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <p>{children}</p>
    </div>
  );
}

// Helpful-poll widget. Pre-vote: shows "Did you find this helpful?" with Yes/No
// buttons. Post-vote (or once another barber's vote bumps the count for a
// lesson the user already voted on): shows "X out of Y barbers found this
// helpful" with a green progress bar + a small (You voted Yes/No) marker so
// the barber sees their own vote reflected in the aggregate.
function HelpfulPoll({
  lessonId,
  currentVote,
  submittingVote,
  onVote,
  existingNote,
}: {
  lessonId: string;
  currentVote: 'yes' | 'no' | null;
  submittingVote: 'yes' | 'no' | null;
  onVote: (v: 'yes' | 'no') => void;
  /** The barber's previously-saved reason for a "no" vote, if any. */
  existingNote: string | null;
}) {
  const t = useT();
  const toast = useToast();
  const stats = useLessonHelpfulStats(currentVote ? lessonId : null);

  if (!currentVote) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[13px] text-ink-500 font-medium mr-1">
          {t('lesson.helpful.q')}
        </span>
        <VoteButton
          label={t('lesson.helpful.yes')}
          kind="yes"
          active={false}
          loading={submittingVote === 'yes'}
          onClick={() => onVote('yes')}
        />
        <VoteButton
          label={t('lesson.helpful.no')}
          kind="no"
          active={false}
          loading={submittingVote === 'no'}
          onClick={() => onVote('no')}
        />
      </div>
    );
  }

  // Optimistic display: the barber's own vote is guaranteed to be in the DB
  // by the time we render this branch (currentVote is read from the same
  // user_lesson_state row), so totals must be at least 1. If the RPC hasn't
  // returned yet (or errored), fall back to the user's local vote — that way
  // we never get stuck on a stale "Counting votes…" message.
  const rawYes = stats.data?.yes_count ?? 0;
  const rawTotal = stats.data?.total ?? 0;
  const yes = Math.max(rawYes, currentVote === 'yes' ? 1 : 0);
  const total = Math.max(rawTotal, 1);
  const percent = Math.round((yes / total) * 100);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <p className="text-[13px] text-ink-700 font-medium">
          {t(total === 1 ? 'lesson.helpful.result_one' : 'lesson.helpful.result_other', {
            yes,
            total,
          })}
        </p>
        <span
          className={`text-[11px] font-extrabold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ${
            currentVote === 'yes'
              ? 'bg-green-50 dark:bg-green-600/20 text-green-600 dark:text-green-300'
              : 'bg-bg-alt text-ink-500 border border-line'
          }`}
        >
          {t(currentVote === 'yes' ? 'lesson.helpful.youYes' : 'lesson.helpful.youNo')}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-line overflow-hidden">
        <div
          className="h-full bg-green-600 transition-[width] duration-500"
          style={{ width: `${percent}%` }}
          aria-hidden
        />
      </div>

      {/* "What missed?" — only on a 'no' vote. Captures qualitative
          beta-feedback signal that the binary poll can't. Shows the
          existing note as the starting value if the barber comes back
          to refine it. */}
      {currentVote === 'no' && (
        <NoVoteFeedback
          lessonId={lessonId}
          existingNote={existingNote}
          onSaved={() => toast(t('lesson.helpful.noteThanks'), 'success')}
        />
      )}
    </div>
  );
}

/** Inline note prompt that appears under a "no" vote. The textarea
 *  starts collapsed (just a button "Tell us why?") so it's not
 *  visually heavy by default; expanding reveals the textarea + save
 *  CTA. Saved notes show as a passive line with an Edit affordance. */
function NoVoteFeedback({
  lessonId,
  existingNote,
  onSaved,
}: {
  lessonId: string;
  existingNote: string | null;
  onSaved: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [editing, setEditing] = useState<boolean>(!existingNote);
  const [note, setNote] = useState<string>(existingNote ?? '');
  const [saving, setSaving] = useState(false);

  // Sync external prop changes (e.g., user toggles to yes then back to no).
  useEffect(() => {
    setNote(existingNote ?? '');
    if (existingNote) setEditing(false);
  }, [existingNote]);

  async function handleSave() {
    setSaving(true);
    const { error } = await setLessonReflectionNote(lessonId, note);
    setSaving(false);
    if (error) {
      toast(t('lesson.helpful.noteError', { message: error.message }), 'error');
      return;
    }
    setEditing(false);
    onSaved();
  }

  if (!editing && existingNote) {
    return (
      <div className="rounded-lg border border-line bg-surface px-3 py-2 flex items-start gap-2">
        <span className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mt-0.5 shrink-0">
          {t('lesson.helpful.noteSavedLabel')}
        </span>
        <p className="flex-1 text-[13px] leading-snug text-ink-700 break-words">{existingNote}</p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[11px] font-bold tracking-wide text-navy-900 dark:text-gold-500 hover:underline shrink-0"
        >
          {t('lesson.helpful.noteEdit')}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-line bg-surface p-3 space-y-2">
      <label className="block text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
        {t('lesson.helpful.noteLabel')}
      </label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t('lesson.helpful.notePlaceholder')}
        rows={2}
        autoFocus
        className="w-full px-2.5 py-2 rounded-md border border-line bg-bg-alt text-ink-900 text-[13px] placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500 resize-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || note.trim().length === 0}
          className="h-9 px-3 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 text-xs font-bold disabled:opacity-50"
        >
          {saving ? t('lesson.helpful.noteSaving') : t('lesson.helpful.noteSave')}
        </button>
        {existingNote && (
          <button
            type="button"
            onClick={() => {
              setNote(existingNote);
              setEditing(false);
            }}
            className="h-9 px-3 rounded-lg border border-line bg-bg-alt text-xs font-bold text-ink-700"
          >
            {t('lesson.helpful.noteCancel')}
          </button>
        )}
      </div>
    </div>
  );
}

function VoteButton({
  label,
  kind,
  active,
  loading,
  onClick,
}: {
  label: string;
  kind: 'yes' | 'no';
  active: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  const base =
    'h-9 px-4 rounded-[10px] text-[13px] font-bold transition-all inline-flex items-center gap-1';
  let variant: string;
  if (active) {
    variant =
      kind === 'yes'
        ? 'bg-green-50 dark:bg-green-600/20 border-[1.5px] border-green-600 text-green-600 dark:text-green-300'
        : 'bg-bg-alt border-[1.5px] border-ink-500 text-ink-700';
  } else {
    variant =
      'bg-surface border-[1.5px] border-line-strong text-ink-700 hover:border-navy-700 dark:hover:border-gold-400';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`${base} ${variant} ${loading ? 'opacity-60' : ''}`}
    >
      {loading ? '…' : label}
    </button>
  );
}

function BookmarkIcon({ className, filled }: { className?: string; filled: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
